import {
    Autocomplete,
    Avatar, Box,
    Button, CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider, FormControl,
    Grid, IconButton,
    InputLabel, MenuItem, OutlinedInput,
    Select, Stack, TextField,
} from "@mui/material";
import LightTextField from "components/LightTextField";
import {useFormik} from "formik";
import React, {useEffect, useState} from "react";
import * as Yup from "yup";
import {LoadingButton} from "@mui/lab";
import useTitle from "../../hooks/useTitle";
import {DriverFormData} from "../../types/Driver";
import {createDriverProfile, getNonDrivers} from "../../api/user";
import ButtonWrapper from "../../components/ButtonWrapper";
import UploadButton from "../../components/UploadButton";
import {PhotoCamera} from "@mui/icons-material";
import {H6, Small} from "../../components/Typography";
import {User} from "../../types/user";


type RegisterDriverDialogProps = {
    open?: boolean,
    onClose?: () => void,
    initialData?: DriverFormData,
    onCreateEntityStatusChange?: (state?: "success" | "error", message?: string) => void,
    action?: "create" | "update"
}

const RegisterDriverDialog = (props: RegisterDriverDialogProps) => {
    useTitle("Enregistrer un Conducteur")
    const [saving, setSaving] = useState(false)
    const [showNonDriversDropdown, setShowNonDriversDropdown] = useState(false)
    const [loadingNonDrivers, setLoadingNonDrivers] = useState<boolean>(false)
    const [selectedUser, setSelectedUser] = useState<User>()
    const [nonDrivers, setNonDrivers] = useState<User[]>([])
    const [drivingLicenseImg, setDrivingLicenseImg] = useState<DriverFormData['drivingLicenseImg']>({})
    const [drivingLicenseExpiry, setDrivingLicenseExpiry] = useState<DriverFormData['drivingLicenseExpiry']>({
        issued: new Date().getTime(),
        expires: new Date().getTime()
    })

    const handleCloseDialog = () => {
        values = {}
        props.onClose()
    }

    const loadNonDrivers = async () => {
        setLoadingNonDrivers(true)
        const nonDriversResult = await getNonDrivers()
        setNonDrivers(nonDriversResult.data)
        setLoadingNonDrivers(false)
    }

    const handleOpenNonDriversDropDown = () => {
        setShowNonDriversDropdown(true)
    }

    const handleCloseNonDriversDropDown = () => {
        setShowNonDriversDropdown(false)
    }


    const handlePostCreateSuccess = () => {
        props.onCreateEntityStatusChange("success")
        setSaving(false)
        handleCloseDialog()
    }

    const handlePostCreateError = (reason) => {
        props.onCreateEntityStatusChange("error", reason)
        setSaving(false)
        if (props.action === "update")
            handleCloseDialog()
    }

    const handleDrivingLicenseExpiryChange = (target: "issued" | "expires", value: number) => {
        setDrivingLicenseExpiry(prevState => {
            return {
                ...prevState,
                [target]: value
            }
        })
    }

    const formatDate = (dateObject: Date) => {
        const stringDate = dateObject.toISOString()
        return stringDate.substring(0, stringDate.lastIndexOf("T"))
    }

    const handleDrivingLicenseUploaded = (target: "front" | "back", files: FileList) => {
        if (!files[0])
            return

        const reader = new FileReader()
        reader.readAsDataURL(files[0])
        reader.onload = () => {
            setDrivingLicenseImg(prevState => {
                return {
                    ...prevState,
                    [target]: {
                        base64: reader.result
                    }
                }
            })
        }

        reader.onerror = (error) => {
            console.error("Error converting image:", error);
        }
    }

    const driverStatusMap = {
        'PENDING': "En Cours",
        "VERIFIED": "Approuvé",
        "REJECTED": "Rejeté",
    }

    const driverGenderMap = {
        'male': "Masculin",
        "female": "Féminin",
    }

    const initialValues: DriverFormData = {
        name: props.initialData?.name || "",
        carName: props.initialData?.carName || "",
        licensePlate: props.initialData?.licensePlate || "",
        status: props.initialData?.status || "PENDING",
        drivingLicenseId: props.initialData?.drivingLicenseId || "",
        dateOfBirth: props.initialData?.dateOfBirth,
        emergencyContact: props.initialData?.emergencyContact || "",
        gender: props.initialData?.gender || "male"
    }

    const handleRegisterDriver = async () => {
        setSaving(true)
        createDriverProfile({
            ...values,
            userId: selectedUser.id,
            drivingLicenseImg: {
                back: {base64: drivingLicenseImg.back.base64.split(',')[1]},
                front: {base64: drivingLicenseImg.front.base64.split(',')[1]},
            },
            drivingLicenseExpiry,
            dateOfBirth: new Date(values['dateOfBirth']).getTime()
        })
            .then(handlePostCreateSuccess)
            .catch(handlePostCreateError)
    }

    const validationSchema = Yup.object().shape({
        name: Yup.string().required("Ce champs est obligatoire!"),
        carName: Yup.string().required("Ce champs est obligatoire!"),
        licensePlate: Yup.string().required("Ce champs est obligatoire!"),
        dateOfBirth: Yup.string().required("Ce champs est obligatoire!"),
        drivingLicenseId: Yup.string().required("Ce champs est obligatoire!"),
        emergencyContact: Yup.string().required("Ce champs est obligatoire!"),
    })


    let {values, errors, handleChange, handleSubmit, touched} = useFormik({
        initialValues,
        validationSchema,
        onSubmit: () => {
            handleRegisterDriver()
        },
    })

    useEffect(() => {
        if (showNonDriversDropdown && nonDrivers.length === 0)
            loadNonDrivers()
    }, [showNonDriversDropdown])


    return (
        <Dialog
            maxWidth={"sm"}
            open={props.open}
            onClose={props.onClose}>
            <DialogTitle sx={{fontSize: "large"}}>Enregistrer un conducteur</DialogTitle>
            <Divider/>
            <DialogContent>
                <form onSubmit={handleSubmit} id={"driver-registration-form"}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Stack gap={1} padding={3} alignItems={"center"} justifyContent={"center"}>
                                <H6>Photo du permis de conduire</H6>

                                <Stack gap={1} padding={1}>
                                    <Small>
                                        Photo Avant
                                    </Small>
                                    <ButtonWrapper>
                                        <UploadButton>

                                            <label htmlFor="front-img-upload-btn">
                                                <input
                                                    required
                                                    accept="image/*"
                                                    id="front-img-upload-btn"
                                                    type="file"
                                                    style={{display: "none"}}
                                                    onChange={event => handleDrivingLicenseUploaded("front", event.target.files)}
                                                />
                                                <IconButton size={"small"} component="span">
                                                    {drivingLicenseImg.front ?
                                                        <img alt={"Driving License Front Image"}
                                                             src={drivingLicenseImg.front.base64}
                                                             style={{
                                                                 objectFit: "cover",
                                                                 borderRadius: 12,
                                                                 width: 220,
                                                                 height: 100,
                                                             }}/> :
                                                        <Box width={220} display={"flex"} padding={4}
                                                             alignItems={"center"} justifyContent={"center"}
                                                             height={100} borderColor={"primary.600"}
                                                             borderRadius={"15px"}
                                                             sx={{borderStyle: "dashed", borderWidth: 1.5}}>

                                                            <PhotoCamera sx={{color: "white"}}/>

                                                        </Box>}
                                                </IconButton>
                                            </label>
                                        </UploadButton>
                                    </ButtonWrapper>
                                </Stack>

                                <Stack gap={1} padding={1}>
                                    <Small>
                                        Photo Arrière
                                    </Small>
                                    <ButtonWrapper>
                                        <UploadButton>

                                            <label htmlFor="back-img-upload-btn">
                                                <input
                                                    required
                                                    accept="image/*"
                                                    id="back-img-upload-btn"
                                                    type="file"
                                                    style={{display: "none"}}
                                                    onChange={event => handleDrivingLicenseUploaded("back", event.target.files)}
                                                />
                                                <IconButton size={"small"} component="span">
                                                    {drivingLicenseImg.back ?
                                                        <img
                                                            alt={"Driving License Back Image"}
                                                            src={drivingLicenseImg.back.base64}
                                                            style={{
                                                                objectFit: "cover",
                                                                borderRadius: 12,
                                                                width: 220,
                                                                height: 100,
                                                            }}/> :

                                                        <Box width={220} display={"flex"} padding={4}
                                                             alignItems={"center"} justifyContent={"center"}
                                                             height={100} borderColor={"primary.600"}
                                                             borderRadius={"15px"}
                                                             sx={{borderStyle: "dashed", borderWidth: 1.5}}>

                                                            <PhotoCamera sx={{color: "white"}}/>

                                                        </Box>}

                                                </IconButton>
                                            </label>
                                        </UploadButton>
                                    </ButtonWrapper>
                                </Stack>

                            </Stack>
                        </Grid>

                        <Grid item xs={12}>
                            <Autocomplete
                                sx={{width: "100%"}}
                                open={showNonDriversDropdown}
                                onOpen={handleOpenNonDriversDropDown}
                                onClose={handleCloseNonDriversDropDown}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                getOptionLabel={(option) => option.name}
                                options={nonDrivers}
                                loading={loadingNonDrivers}
                                onChange={(event, value) => setSelectedUser(value)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Selectionner un utilisateur"
                                        //@ts-ignore
                                        slotProps={{
                                            input: {
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <React.Fragment>
                                                        {loadingNonDrivers ?
                                                            <CircularProgress color={"success"} size={20}/> : null}
                                                        {params.InputProps.endAdornment}
                                                    </React.Fragment>
                                                ),
                                            },
                                        }}
                                    />
                                )}
                            />

                        </Grid>


                        <Grid item xs={12} md={6}>
                            <LightTextField
                                fullWidth
                                name="name"
                                label="Nom du conducteur"
                                placeholder="Nom du conducteur"
                                value={values.name}
                                onChange={handleChange}
                                error={Boolean(touched.name && errors.name)}
                                helperText={touched.name && errors.name}
                            />

                        </Grid>

                        <Grid item xs={12} md={6}>
                            <LightTextField
                                fullWidth
                                name="carName"
                                label="Modèle de Voiture"
                                placeholder="Modèle de Voiture"
                                value={values.carName}
                                onChange={handleChange}
                                error={Boolean(touched.carName && errors.carName)}
                                helperText={touched.carName && errors.carName}
                            />
                        </Grid>


                        <Grid item xs={12} md={6}>
                            <LightTextField
                                fullWidth
                                name="licensePlate"
                                label="Plaque d'immatriculation"
                                placeholder="Plaque d'immatriculation"
                                value={values.licensePlate}
                                onChange={handleChange}
                                error={Boolean(touched.licensePlate && errors.licensePlate)}
                                helperText={touched.licensePlate && errors.licensePlate}
                            />
                        </Grid>


                        <Grid item xs={12} md={6}>
                            <LightTextField
                                fullWidth
                                name="drivingLicenseId"
                                label="Numéro du permis de conduire"
                                placeholder="Numéro du permis de conduire"
                                value={values.drivingLicenseId}
                                onChange={handleChange}
                                error={Boolean(touched.drivingLicenseId && errors.drivingLicenseId)}
                                helperText={touched.drivingLicenseId && errors.drivingLicenseId}
                            />
                        </Grid>

                        <Grid item container xs={12} spacing={2}>
                            <Grid item xs={12}>
                                <H6>Expiration du permis de conduire</H6>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <LightTextField
                                    fullWidth
                                    label="Date de délivrance"
                                    type={"date"}
                                    value={formatDate(new Date(drivingLicenseExpiry.issued))}
                                    onChange={event => handleDrivingLicenseExpiryChange("issued", new Date(event.target.value).getTime())}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <LightTextField
                                    fullWidth
                                    label="Date d'expiration"
                                    type={"date"}
                                    value={formatDate(new Date(drivingLicenseExpiry.expires))}
                                    onChange={event => handleDrivingLicenseExpiryChange("expires", new Date(event.target.value).getTime())}
                                />
                            </Grid>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <InputLabel id="demo-simple-select-label">Statut d'approbation</InputLabel>
                                <Select value={values['status']}
                                        sx={{textTransform: "capitalize"}}
                                        name={"status"}
                                        onChange={handleChange}
                                        input={<OutlinedInput id="select-multiple-chip" label="Chip"/>}
                                        label="Statut d'approbation">
                                    {['PENDING', 'VERIFIED', 'REJECTED']?.map(status =>
                                        <MenuItem sx={{textTransform: "capitalize"}}
                                                  value={status}>{driverStatusMap[status]}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <LightTextField
                                fullWidth
                                name="dateOfBirth"
                                label="Date de naissance"
                                value={values.dateOfBirth}
                                onChange={handleChange}
                                type={"date"}
                                error={Boolean(touched.dateOfBirth && errors.dateOfBirth)}
                                helperText={touched.dateOfBirth && errors.dateOfBirth}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <LightTextField
                                fullWidth
                                name="emergencyContact"
                                label="Numéro d'urgence"
                                placeholder="Numéro d'urgence"
                                value={values.emergencyContact}
                                onChange={handleChange}
                                type={"tel"}
                                error={Boolean(touched.emergencyContact && errors.emergencyContact)}
                                helperText={touched.emergencyContact && errors.emergencyContact}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <InputLabel id="demo-simple-select-label">Sexe</InputLabel>
                                <Select value={values['gender']}
                                        sx={{textTransform: "capitalize"}}
                                        name={"gender"}
                                        onChange={handleChange}
                                        input={<OutlinedInput id="select-multiple-chip" label="Chip"/>}
                                        label="Statut du Conducteur">
                                    {['female', 'male']?.map(gender =>
                                        <MenuItem sx={{textTransform: "capitalize"}}
                                                  value={gender}>{driverGenderMap[gender]}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>

                    </Grid>

                </form>
            </DialogContent>
            <Divider/>
            <DialogActions>
                <Button variant={"outlined"} onClick={handleCloseDialog}>Annuler</Button>
                <LoadingButton type="submit" variant="contained" form={"driver-registration-form"} loading={saving}>
                    Enregistrer
                </LoadingButton>
            </DialogActions>

        </Dialog>
    )
}

export default RegisterDriverDialog
